import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { WorkShiftInvoiceTasksDto, WorkShiftPlanDto } from '@sr/dto';
import { workShiftReminderActions } from 'entities/salary/work-shifts/work-shift-reminder.slice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { useActionCreators, useStateSelector } from 'shared/store';
import { useSnack } from 'shared/ui/snack';
import { invoiceLinkManager, salaryLinkManager } from 'utils/link-manager';
import { formatPersonPruning } from 'utils/person-format-helpers';
import { workShiftsApi } from './api';
import { PlanCounter, PlanInfoCard } from './plan-counter';

export const TodayWorkShiftDialog = () => {
  const reminderDate = useStateSelector((state) => state.ui.workShiftReminder.date);
  const [isOpenedDialogVisible, setIsOpenedDialogVisible] = useState(false);
  workShiftsApi.useGetTodayWorkShiftPlanQuery();

  const { openReminder, closeReminder } = useActionCreators(workShiftReminderActions);
  const { data: todayPlan } = workShiftsApi.useGetTodayWorkShiftPlanQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data, isLoading, error, refetch } = workShiftsApi.useGetTodayWorkShiftPlanQuery(
    reminderDate ? { date: reminderDate } : undefined,
    { skip: !reminderDate, refetchOnMountOrArgChange: true }
  );
  const [openShift] = workShiftsApi.useOpenWorkShiftMutation();
  const { showError, showSuccess } = useSnack();

  const shift = data?.shift;
  const open = !!reminderDate;
  const workShiftsLink = `/${salaryLinkManager.baseUrl}/${salaryLinkManager.tabs.workShifts}`;
  const isMandatory = !!data?.shouldOpenShift;

  const isShiftAcceptedRef = useRef(false);
  useEffect(() => {
    if (todayPlan?.shouldOpenShift && !reminderDate && !isShiftAcceptedRef.current) openReminder(todayPlan.date);
  }, [todayPlan?.shouldOpenShift, todayPlan?.date, reminderDate, openReminder]);

  const handleCloseReminder = () => {
    closeReminder();
  };

  const handleCloseOpenedDialog = () => {
    setIsOpenedDialogVisible(false);
  };

  useEffect(() => {
    if (reminderDate) refetch();
  }, [reminderDate, refetch]);

  const reminderLabel = useMemo(() => {
    if (!reminderDate) return '';
    const [year, month, day] = reminderDate.split('-');
    return `${day}.${month}.${year}`;
  }, [reminderDate]);

  const handleOpenShift = async () => {
    try {
      await openShift().unwrap();
      isShiftAcceptedRef.current = true;
      showSuccess('План на день открыт');
      closeReminder();
      setIsOpenedDialogVisible(true);
    } catch (e) {
      showError('Ошибка открытия плана на день', e);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={isMandatory ? undefined : handleCloseReminder}
        disableEscapeKeyDown={isMandatory}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{reminderLabel ? `План на день · ${reminderLabel}` : 'План на день'}</DialogTitle>
        <DialogContent>
          {isLoading && <Typography>Загрузка...</Typography>}
          {error && <Typography color="error">{extractNestJsErrorMessage(error)}</Typography>}
          {data && !isLoading && (
            <Stack spacing={2.5}>
              <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <PlanCounter title="Звонки" fact={data.callsFact} plan={data.callsPlan} />
                  <PlanCounter title="КП" fact={data.offersFact} plan={data.offersPlan} />
                  <PlanInfoCard title="Лиды «Ждем решения»" value={data.waitingLeadsCount} />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Смена закрывается только при выполнении плана по звонкам и КП
                </Typography>
              </Box>

              <Divider />

              <InvoiceTasksSection plan={data} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {!isMandatory && <Button onClick={handleCloseReminder}>Закрыть</Button>}
          {!shift && <Button variant="contained" onClick={handleOpenShift}>Старт</Button>}
          {shift && !shift.closedAt && <Button component={Link} to={workShiftsLink} onClick={handleCloseReminder}>К плану на день</Button>}
        </DialogActions>
      </Dialog>
      <Dialog open={isOpenedDialogVisible} onClose={handleCloseOpenedDialog} maxWidth="xs" fullWidth>
        <DialogTitle>План на день открыт</DialogTitle>
        <DialogContent>
          <Typography>
            План на день открыт. Закрыть его нужно в конце рабочего дня в разделе Зарплата / План на день.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOpenedDialog}>Закрыть</Button>
          <Button component={Link} to={workShiftsLink} variant="contained" onClick={handleCloseOpenedDialog}>
            К плану на день
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const InvoiceTasksSection = ({ plan }: { plan: WorkShiftPlanDto }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const total = plan.paidInvoicesWithoutWcc.total + plan.debtInvoices.total;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setIsExpanded((expanded) => !expanded)}
        sx={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <Typography variant="subtitle1">Задачи по счетам</Typography>
        <Chip size="small" label={total} color={total ? 'warning' : 'default'} variant={total ? 'filled' : 'outlined'} />
        <Box flexGrow={1} />
        <IconButton size="small" sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
          <ExpandMoreIcon />
        </IconButton>
      </Stack>
      <Collapse in={isExpanded} unmountOnExit>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <InvoiceTasks title="Оплаченные счета без заявок" tasks={plan.paidInvoicesWithoutWcc} showManagers={plan.showManagers} />
          <InvoiceTasks title="Счета со статусом долг" tasks={plan.debtInvoices} showManagers={plan.showManagers} />
        </Stack>
      </Collapse>
    </Box>
  );
};

const InvoiceTasks = ({ title, tasks, showManagers }: { title: string; tasks: WorkShiftInvoiceTasksDto; showManagers: boolean }) => {
  const { items, total } = tasks;
  const hidden = total - items.length;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: items.length ? 1 : 0 }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Chip size="small" label={total} color={total ? 'warning' : 'default'} variant={total ? 'filled' : 'outlined'} />
      </Stack>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Нет задач</Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
          {items.map((invoice) => (
            <Chip
              key={invoice.id}
              component={Link}
              to={invoiceLinkManager.link(invoice.id)}
              clickable
              size="small"
              variant="outlined"
              label={showManagers && invoice.manager ? `${invoice.invoiceNumber} · ${formatPersonPruning(invoice.manager)}` : invoice.invoiceNumber}
            />
          ))}
        </Stack>
      )}
      {hidden > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Показаны первые {items.length}, еще {hidden} не поместились
        </Typography>
      )}
    </Paper>
  );
};
